import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/api/client";

/** What a page renders from: a spinner, an error message (the backend's `errors` joined),
 * or the loaded data — never a half-loaded mix of the three. */
type AsyncState<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; data: T };

/** Runs `fn` on mount and whenever `deps` changes, exposing a `reload` callback for
 * actions that need to re-fetch after a mutation (every page here does that at least
 * once — recording a review verdict, signing off, committing an observation). */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    setState({ status: "loading" });
    fn()
      .then((data) => setState({ status: "ready", data }))
      .catch((err: unknown) => {
        const message =
          err instanceof ApiError ? err.errors.join("; ") : err instanceof Error ? err.message : String(err);
        setState({ status: "error", message });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  useEffect(load, [load]);

  return { ...state, reload: () => setTick((t) => t + 1) };
}
